import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaSearch, FaFileExcel, FaTimes,
  FaChevronDown, FaChevronUp, FaMoneyBillWave
} from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './Salary.css';

const getCrLedgerFromSalary = (sal) => {
  const mode = (sal.paymentMode || 'Cash').trim();
  if (mode.toLowerCase() === 'cash') return 'Cash';
  const bankName = (sal.bankName || sal.bank?.name || '').trim();
  return bankName || mode;
};

const mapApiSalaryToRow = (sal) => {
  const empName = (sal.employee?.name || '').trim();
  const deptName = (sal.employee?.department?.name || '').trim();
  const drLedger = deptName ? `${empName} (${deptName})` : empName;

  const amount = Number(sal.netSalary) || 0;

  return {
    _id: sal._id,
    date: sal.paidAt,
    vchNo: (sal.slipNo || '').trim(),
    drLedger,
    crLedger: getCrLedgerFromSalary(sal),
    amountDr: amount,
    amountCr: amount,
    narration: (sal.narration || '').trim(),
    college: sal.college,
    paymentMode: sal.paymentMode,
    bankName: sal.bankName,
    bank: sal.bank,
  };
};

const getDrLedger = (sal) => sal.drLedger ?? '';
const getCrLedger = (sal) => sal.crLedger || getCrLedgerFromSalary(sal);
const getVchNo = (sal) => sal.vchNo ?? '';
const getNarration = (sal) => sal.narration ?? '';

const Salary = () => {
  const { user } = useAuth();
  const { colleges: collegesList, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (isScoped && collegesList.length > 0 && !filterCollege) {
      setFilterCollege(collegesList[0]._id);
    }
  }, [isScoped, collegesList, filterCollege]);

  const fetchSalaries = useCallback(async () => {
    if (!filterCollege) {
      setSalaries([]);
      setFetchError('');
      return;
    }

    setLoading(true);
    setFetchError('');
    setSalaries([]);

    try {
      const { data } = await api.get('/salary', { params: { college: filterCollege, _t: Date.now() } });
      const paid = (data?.data || [])
        .filter((s) => s.isPaid && String(s.college?._id || s.college) === String(filterCollege));

      let rows = paid.map(mapApiSalaryToRow);

      if (fromDate || toDate) {
        rows = rows.filter((row) => {
          if (!row.date) return false;
          const d = new Date(row.date);
          if (fromDate) {
            const f = new Date(fromDate);
            f.setHours(0, 0, 0, 0);
            if (d < f) return false;
          }
          if (toDate) {
            const t = new Date(toDate);
            t.setHours(23, 59, 59, 999);
            if (d > t) return false;
          }
          return true;
        });
      }

      rows.sort((a, b) => new Date(a.date) - new Date(b.date));
      setSalaries(rows);
    } catch (err) {
      setFetchError(err.response?.data?.message || 'Failed to load salary data');
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  }, [filterCollege, fromDate, toDate]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const handleClearFilters = () => {
    setSearchQuery('');
    if (!isScoped) setFilterCollege('');
    setFromDate('');
    setToDate('');
    if (!isScoped) setSalaries([]);
  };

  const filteredSalaries = useMemo(() => {
    const search = searchQuery.toLowerCase().trim();
    return salaries.filter((sal) => {
      if (!search) return true;
      const drLedger = getDrLedger(sal).toLowerCase();
      const crLedger = getCrLedger(sal).toLowerCase();
      const narration = getNarration(sal).toLowerCase();
      const vchNo = getVchNo(sal).toLowerCase();
      return (
        drLedger.includes(search) ||
        crLedger.includes(search) ||
        narration.includes(search) ||
        vchNo.includes(search)
      );
    });
  }, [salaries, searchQuery]);

  const selectedCollege = useMemo(
    () => collegesList.find((c) => String(c._id) === String(filterCollege)),
    [collegesList, filterCollege]
  );

  const totalAmountDr = useMemo(
    () => filteredSalaries.reduce((sum, sal) => sum + (Number(sal.amountDr) || 0), 0),
    [filteredSalaries]
  );

  const totalAmountCr = useMemo(
    () => filteredSalaries.reduce((sum, sal) => sum + (Number(sal.amountCr) || 0), 0),
    [filteredSalaries]
  );

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleExportExcel = () => {
    const headers = [
      'Date', 'VCh No', 'Dr Ledger', 'Cr Ledger', 'Amount Dr', 'Amount Cr', 'Narations',
    ];

    const rows = filteredSalaries.map((sal) => [
      sal.date ? new Date(sal.date).toLocaleDateString('en-GB') : '',
      getVchNo(sal),
      getDrLedger(sal),
      getCrLedger(sal),
      Number(sal.amountDr) || 0,
      Number(sal.amountCr) || 0,
      getNarration(sal),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 35 },
      { wch: 25 },
      { wch: 14 },
      { wch: 14 },
      { wch: 45 },
    ];

    const filenameParts = ['Salary_Register'];
    if (selectedCollege) {
      const cleanName = selectedCollege.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/__+/g, '_');
      filenameParts.push(cleanName);
    }
    if (fromDate && toDate) filenameParts.push(`${fromDate}_to_${toDate}`);
    else if (fromDate) filenameParts.push(`from_${fromDate}`);
    else if (toDate) filenameParts.push(`to_${toDate}`);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Salary Register');
    XLSX.writeFile(wb, `${filenameParts.join('_')}.xlsx`);
  };

  const showSelectCollegeMsg = !filterCollege && !collegesLoading;

  return (
    <div className="tally-export-employee-salary-container">
      <div className="tally-export-employee-salary-header-row">
        <h2 className="tally-export-employee-salary-title">
          <FaMoneyBillWave /> Salary Disbursement Register
        </h2>
        <div className="tally-export-employee-salary-export-btns">
          <button
            type="button"
            className="tally-export-employee-salary-btn-excel"
            onClick={handleExportExcel}
            disabled={filteredSalaries.length === 0}
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {filterCollege && filteredSalaries.length > 0 && (
        <div className="tally-export-employee-salary-summary-strip">
          <span>
            Showing <strong>{filteredSalaries.length}</strong> paid salaries
            {selectedCollege ? <> for <strong>{selectedCollege.name}</strong></> : null}
          </span>
          <div className="tally-export-employee-salary-summary-amounts">
            <span className="tally-export-employee-salary-summary-amount">
              Total Dr: <strong>₹ {totalAmountDr.toLocaleString()}</strong>
            </span>
            <span className="tally-export-employee-salary-summary-amount" style={{ marginLeft: '15px' }}>
              Total Cr: <strong>₹ {totalAmountCr.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}

      <div className="tally-export-employee-salary-controls">
        <div className="tally-export-employee-salary-search-wrapper">
          <FaSearch className="tally-export-employee-salary-search-icon" />
          <input
            type="text"
            className="tally-export-employee-salary-search-input"
            placeholder="Search employee, slip no, bank..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!filterCollege}
          />
        </div>

        {!isScoped && (
          <select
            className="tally-export-employee-salary-select"
            value={filterCollege}
            onChange={(e) => setFilterCollege(e.target.value)}
            disabled={collegesLoading}
          >
            <option value="">Select College</option>
            {collegesList.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {isScoped && selectedCollege && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 14px',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            🏫 {selectedCollege.name}
          </span>
        )}

        <input
          type="date"
          className="tally-export-employee-salary-select"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From Date"
          disabled={!filterCollege}
        />

        <input
          type="date"
          className="tally-export-employee-salary-select"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          title="To Date"
          disabled={!filterCollege}
        />

        {(filterCollege && !isScoped) || fromDate || toDate || searchQuery ? (
          <button type="button" className="tally-export-employee-salary-btn-clear" onClick={handleClearFilters}>
            <FaTimes /> Clear
          </button>
        ) : null}
      </div>

      <div className="tally-export-employee-salary-grouped-container">
        {collegesLoading ? (
          <div className="tally-export-employee-salary-state-msg">Loading colleges...</div>
        ) : showSelectCollegeMsg ? (
          <div className="tally-export-employee-salary-state-msg tally-export-employee-salary-empty">
            Please select a college to view paid salaries for Tally export.
          </div>
        ) : loading ? (
          <div className="tally-export-employee-salary-state-msg">Loading records...</div>
        ) : fetchError ? (
          <div className="tally-export-employee-salary-state-msg tally-export-employee-salary-empty">{fetchError}</div>
        ) : filteredSalaries.length === 0 ? (
          <div className="tally-export-employee-salary-state-msg tally-export-employee-salary-empty">
            No paid salary records found for the selected college and date range.
          </div>
        ) : (
          <div className="tally-export-employee-salary-table-group">
            <h3 className="tally-export-employee-salary-group-header">
              <span className="tally-export-employee-salary-college-name">
                🏫 {selectedCollege?.name || 'College'}
              </span>
              <div className="tally-export-employee-salary-group-stats">
                <span className="tally-export-employee-salary-group-count">
                  {filteredSalaries.length} entries
                </span>
                <span className="tally-export-employee-salary-group-total">
                  Total: ₹ {totalAmountDr.toLocaleString()}
                </span>
              </div>
            </h3>
            <div className="tally-export-employee-salary-table-wrapper">
              <table className="tally-export-employee-salary-table">
                <thead>
                  <tr>
                    <th width="55px">S.No</th>
                    <th className="tally-export-employee-salary-col-date">Date</th>
                    <th className="tally-export-employee-salary-col-vch">VCh No</th>
                    <th className="tally-export-employee-salary-col-dr tally-export-employee-salary-align-left">Dr Ledger</th>
                    <th className="tally-export-employee-salary-col-cr tally-export-employee-salary-align-left">Cr Ledger</th>
                    <th className="tally-export-employee-salary-col-amount-dr">Amount Dr</th>
                    <th className="tally-export-employee-salary-col-amount-cr">Amount Cr</th>
                    <th className="tally-export-employee-salary-col-narration tally-export-employee-salary-align-left">Narrations</th>
                    <th className="tally-export-employee-salary-col-expand-header" width="70px">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSalaries.map((sal, index) => (
                    <React.Fragment key={sal._id || index}>
                      <tr className="tally-export-employee-salary-main-row">
                        <td>{index + 1}</td>
                        <td className="tally-export-employee-salary-col-date" style={{ fontWeight: 600, color: '#334155' }}>
                          {fmtDate(sal.date)}
                        </td>
                        <td className="tally-export-employee-salary-col-vch" style={{ fontWeight: 600, color: '#475569' }}>
                          {getVchNo(sal)}
                        </td>
                        <td className="tally-export-employee-salary-col-dr tally-export-employee-salary-align-left" style={{ fontWeight: 700, color: '#1e3a5f' }}>
                          {getDrLedger(sal)}
                        </td>
                        <td className="tally-export-employee-salary-col-cr tally-export-employee-salary-align-left" style={{ fontWeight: 700, color: '#475569' }}>
                          {getCrLedger(sal)}
                        </td>
                        <td className="tally-export-employee-salary-col-amount-dr tally-export-employee-salary-amt-total">
                          ₹ {Number(sal.amountDr || 0).toLocaleString()}
                        </td>
                        <td className="tally-export-employee-salary-col-amount-cr tally-export-employee-salary-amt-total" style={{ color: '#16a34a' }}>
                          ₹ {Number(sal.amountCr || 0).toLocaleString()}
                        </td>
                        <td className="tally-export-employee-salary-col-narration tally-export-employee-salary-align-left">
                          <div className="tally-export-employee-salary-truncate-text">
                            {getNarration(sal)}
                          </div>
                        </td>
                        <td className="tally-export-employee-salary-col-expand-trigger">
                          <button
                            type="button"
                            className="tally-export-employee-salary-expand-btn"
                            onClick={() => setExpandedRow(expandedRow === sal._id ? null : sal._id)}
                            aria-label="Toggle details"
                          >
                            {expandedRow === sal._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </td>
                      </tr>

                      {expandedRow === sal._id && (
                        <tr className="tally-export-employee-salary-expanded-row">
                          <td colSpan="9" style={{ padding: 0 }}>
                            <div className="tally-export-employee-salary-expanded-content">
                              <div className="tally-export-employee-salary-expanded-grid">
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-date">
                                  <span className="tally-export-employee-salary-exp-label">Date</span>
                                  <span className="tally-export-employee-salary-exp-value">{fmtDate(sal.date)}</span>
                                </div>
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-vch">
                                  <span className="tally-export-employee-salary-exp-label">VCh No</span>
                                  <span className="tally-export-employee-salary-exp-value">{getVchNo(sal) || '—'}</span>
                                </div>
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-dr">
                                  <span className="tally-export-employee-salary-exp-label">Dr Ledger</span>
                                  <span className="tally-export-employee-salary-exp-value">{getDrLedger(sal) || '—'}</span>
                                </div>
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-cr">
                                  <span className="tally-export-employee-salary-exp-label">Cr Ledger</span>
                                  <span className="tally-export-employee-salary-exp-value">{getCrLedger(sal) || '—'}</span>
                                </div>
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-amount-dr">
                                  <span className="tally-export-employee-salary-exp-label">Amount Dr</span>
                                  <span className="tally-export-employee-salary-exp-value" style={{ color: '#dc2626', fontWeight: 700 }}>
                                    ₹ {Number(sal.amountDr || 0).toLocaleString()}
                                  </span>
                                </div>
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-amount-cr">
                                  <span className="tally-export-employee-salary-exp-label">Amount Cr</span>
                                  <span className="tally-export-employee-salary-exp-value" style={{ color: '#16a34a', fontWeight: 700 }}>
                                    ₹ {Number(sal.amountCr || 0).toLocaleString()}
                                  </span>
                                </div>
                                <div className="tally-export-employee-salary-exp-item tally-export-employee-salary-exp-narration">
                                  <span className="tally-export-employee-salary-exp-label">Narration</span>
                                  <span className="tally-export-employee-salary-exp-value">{getNarration(sal) || '—'}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Salary;